import { Ability } from '../hooks/useAuth';
import PublicRoute from 'enums/publicRoutes';
import AdminRoute, { adminPath } from 'enums/adminRoutes';
import type { MetaPixelFunction } from 'utils/metaPixel';

declare global {
  interface Window {
    runConfig: {
      frontendUrl: string;
      backendUrl: string;
      nodeEnv: string;
      mansLauksUrl: string;
      llkcRegistryUrl: string;
      plausibleDomain?: string;
      plausibleApiHost?: string;
      gaMeasurementId?: string;
      metaPixelId?: string;
      reverbHost: string;
      reverbPort: number;
      reverbScheme: 'http' | 'https';
      reverbKey: string;
      googleMapsApiKey?: string;
      googleMapsMapId?: string;
    };
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: MetaPixelFunction;
    _fbq?: MetaPixelFunction;
  }
}

export const routes = {
  api: {
    frontendUrl: window?.runConfig?.frontendUrl,
    baseUrl: window?.runConfig?.backendUrl,
    mansLauksUrl: window?.runConfig?.mansLauksUrl,
    llkcRegistryUrl: window?.runConfig?.llkcRegistryUrl,
  },
  admin: {
    beginning: PublicRoute.HOME,
    adminLanding: adminPath.base(),
    farmTransactions: adminPath.to(AdminRoute.FARM_TRANSACTIONS),
    transactions: adminPath.to(AdminRoute.TRANSACTIONS),
    orders: adminPath.to(AdminRoute.ORDERS),
    farmContracts: adminPath.to(AdminRoute.FARM_CONTRACTS),
    products: adminPath.to(AdminRoute.PRODUCTS),
    coupons: adminPath.to(AdminRoute.COUPONS),
    quality_marks: adminPath.to(AdminRoute.QUALITY_MARKS),
    farmUsers: adminPath.to(AdminRoute.FARM_USERS),
    eShopEmail: adminPath.to(AdminRoute.ESHOP_EMAIL),
    farmEmail: adminPath.to(AdminRoute.FARM_EMAIL),
    qmark: adminPath.to(AdminRoute.QMARK),
    users: adminPath.to(AdminRoute.USERS),
    farms: adminPath.to(AdminRoute.FARMS),
    contracts: adminPath.to(AdminRoute.CONTRACTS),
    roles: adminPath.to(AdminRoute.PERMISSIONS),
    banners: adminPath.to(AdminRoute.BANNERS),
    globalSettings: adminPath.to(AdminRoute.SETTINGS),
    catalogSettings: adminPath.to(AdminRoute.CATALOG_SETTINGS),
    emarketSettings: adminPath.to(AdminRoute.EMARKET_SETTINGS),
    page: adminPath.to(AdminRoute.PAGES),
    notifications: adminPath.to(AdminRoute.NOTIFICATIONS),
    listings: adminPath.to(AdminRoute.LISTINGS),
    chat: adminPath.to(AdminRoute.CHAT),
    classifiers: adminPath.to(AdminRoute.CLASSIFIERS),
    partners: adminPath.to(AdminRoute.PARTNERS),
    adminPartnerDeliveries: adminPath.to(AdminRoute.FARM_DELIVERIES),
    adminBusiness: adminPath.to(AdminRoute.BUSINESS),
  },
};

export const breakpoints = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 1200,
};

export const QUALITY_MARK_ASPECT_RATIO = 1 / 1;

export const exportIds = {
  pdfExportRoot: 'pdf-export-root',
  pdfExportContent: 'pdf-export-content',
};

export const exportConfig = {
  maxProducts: 1000,
};

export const carouselResponsive = [
  { breakpoint: 590, settings: { slidesToShow: 1 } },
  { breakpoint: 900, settings: { slidesToShow: 2 } },
  { breakpoint: 1150, settings: { slidesToShow: 3 } },
  { breakpoint: 1484, settings: { slidesToShow: 4 } },
  { breakpoint: 1760, settings: { slidesToShow: 5 } },
  { breakpoint: 9999, settings: { slidesToShow: 5 } },
];

export const options = {
  filters: {
    pageSizeOptions: [20, 40],
    defaultPageSize: 20,
    defaultDistanceRadius: 5,
  },
  images: {
    defaultProductImage: '/defaults/product-not-found.png',
    defaultImageNotFound: '/defaults/image-not-found.jpeg',
    defaultFarmImage: '/defaults/farm-not-found.png',
    defaultCategoryImage: '/defaults/image-not-found.jpeg',
    defaultBannerOneImage: '/banners/banner_1.png',
    defaultBannerTwoImage: '/banners/banner_2.png',
    defaultBannerThreeImage: '/banners/banner_3.png',
    defaultBannerFourImage: '/banners/banner_4.png',
    defaultFarmGaleryImage: '/defaults/farm_galery.png',
    maxImages: 5,
    maxImageSize: 1440,
    maxSizeInMB: 22,
    allowedFormats: ['image/png', 'image/jpeg', 'image/bmp'],
    bannerImagesCount: {
      banner_1: 1,
      banner_2: 5,
      banner_3: 5,
      banner_4: 5,
    },
  },
  documents: {
    maxFiles: 1,
    maxSizeInMB: 2,
    allowedFormats: ['.docx', '.doc', '.pdf', '.xls', '.xlsx'],
  },
  open_hours: {
    0: { no: '00:00', lidz: '00:00', closed: false },
    1: { no: '00:00', lidz: '00:00', closed: false },
    2: { no: '00:00', lidz: '00:00', closed: false },
    3: { no: '00:00', lidz: '00:00', closed: false },
    4: { no: '00:00', lidz: '00:00', closed: false },
    5: { no: '00:00', lidz: '00:00', closed: false },
    6: { no: '00:00', lidz: '00:00', closed: false },
  },
  toolbarOptions: [
    ['bold', 'italic', 'underline', 'strike'], // toggled buttons
    ['blockquote', 'code-block'],

    [{ header: 1 }, { header: 2 }], // custom button values
    [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
    [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
    [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
    [{ direction: 'rtl' }], // text direction

    [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown

    [{ color: [] }, { background: [] }], // dropdown with defaults from theme
    [{ font: [] }],
    [{ align: [] }],

    ['clean'], // remove formatting button
  ],
  outstafDeliveries: [
    { label: 'Dpd pakomāts', value: 'Dpd pakomāts' },
    { label: 'Omniva pakomāts', value: 'Omniva pakomāts' },
    { label: 'Izipizi pakomāts', value: 'Izipizi pakomāts' },
    { label: 'Venipak pakomāts', value: 'Venipak pakomāts' },
  ],
  autoSave: {
    interval: 3 * 60 * 1000, // 3 minutes
    errorTimeout: 5 * 60 * 1000, // 5 minutes
  },
};

export const links = {
  youtube: 'https://www.youtube.com/@novadagarsa1207/',
  instagram: 'https://www.instagram.com/novadagarsa/',
  facebook: 'https://www.facebook.com/NovadaGarsa/',
  venipak:
    'https://venipak.com/lv/produkti-un-pakalpojumi/pakalpojumi-e-komercijai/#Modulis-e-veikaliem',
  izipizi: 'https://business.izipiziapp.com/',
  omniva: 'https://www.omniva.lv/bizness/api/',
  dpd: 'https://eserviss.dpd.lv/order',
};

export const policyLinks = {
  cookiePolicy: 'https://novadagarsa.lv/llkc-sikdatnu-politika/',
  privacyPolicy: 'https://novadagarsa.lv/privatuma-politika/',
  termsOfUse: 'https://novadagarsa.lv/lietosanas-noteikumi-distances-ligums/',
};

const getMarketUrl = () => {
  return window?.runConfig?.frontendUrl || 'https://tirgus.novadagarsa.lv';
};

export const headerLinks = {
  main: 'https://novadagarsa.lv/par-novadu-garsa/',
  about: 'https://novadagarsa.lv/par-novadu-garsa/',
  current_events: 'https://novadagarsa.lv/aktualitates/',
  contests: 'https://novadagarsa.lv/produktu-kvalitates-konkurss/',
  recipes: 'https://novadagarsa.lv/receptes/',
  events: 'https://novadagarsa.lv/pasakumi/',
  contacts: 'https://novadagarsa.lv/kontakti/',
  market: getMarketUrl(),
};

export const OrderPaymentStatusEnum = {
  1: 'initialized_payment',
  2: 'failed',
  3: 'payment_system_failed',
  4: 'success',
} as const;

export const permissions = {
  admin_panel: 'admin_panel' as Ability,
  view_company: 'view_company' as Ability,
  edit_company: 'edit_company' as Ability,
  view_transaction: 'view_transaction' as Ability,
  edit_transaction: 'edit_transaction' as Ability,
  view_contract: 'view_contract' as Ability,
  edit_contract: 'edit_contract' as Ability,
  view_delivery: 'view_delivery' as Ability,
  edit_delivery: 'edit_delivery' as Ability,
  view_product: 'view_product' as Ability,
  edit_product: 'edit_product' as Ability,
  view_coupon: 'view_coupon' as Ability,
  edit_coupon: 'edit_coupon' as Ability,
  view_quality_mark: 'view_quality_mark' as Ability,
  edit_quality_mark: 'edit_quality_mark' as Ability,
  view_partner: 'view_partner' as Ability,
  edit_partner: 'edit_partner' as Ability,
  view_farm_user: 'view_farm_user' as Ability,
  edit_farm_user: 'edit_farm_user' as Ability,
  view_farm_listing: 'view_farm_listing' as Ability,
  edit_farm_listing: 'edit_farm_listing' as Ability,
};

const makeFields = (count: number) =>
  Array.from({ length: count }, (_, i) => ({ name: `url_${i}` }));

export const bannerImages = {
  banner_1: makeFields(1),
  banner_2: makeFields(5),
  banner_3: makeFields(5),
  banner_4: makeFields(5),
};

export const realtime = {
  apiUrl: window?.runConfig?.backendUrl,
  reverbHost: window?.runConfig?.reverbHost,
  reverbPort: window?.runConfig?.reverbPort,
  reverbScheme: window?.runConfig?.reverbScheme,
  reverbKey: window?.runConfig?.reverbKey,
};

export const features = {
  mansLauks: ['staging', 'development'].includes(
    window?.runConfig?.nodeEnv ?? ''
  ),
};

export const googleMaps = {
  apiKey: window?.runConfig?.googleMapsApiKey ?? '',
  mapId: window?.runConfig?.googleMapsMapId || 'DEMO_MAP_ID',
};
